<template>
  <div class="page-wrapper">
    <div class="container">
      <header class="header">
        <h1>{{ getReportTitle() }}</h1>
        <div style="width: 24px;" />
      </header>

      <div v-if="myDataObj && myDataObj.evaluator" class="user-info">
        <div class="avatar">
          <img src="@/assets/images/pReport/avatar.png" alt="头像">
        </div>
        <div class="user-details">
          <h2>{{ myDataObj.evaluator.name }}</h2>
          <div class="user-info-list">
            <p v-if="getSchoolInfo()">{{ getSchoolInfo() }}</p>
            <p v-if="myDataObj.evaluator.studentNo">账号：{{ myDataObj.evaluator.studentNo }}</p>
            <p v-if="myDataObj.timer && myDataObj.timer.evaluateDate">测评日期：{{ myDataObj.timer.evaluateDate }}</p>
          </div>
        </div>
      </div>

      <!-- 循环显示每份量表的测评结果 -->
      <div
        v-for="(paper, paperIndex) in (myDataObj && myDataObj.papers ? myDataObj.papers : [])"
        :key="paperIndex"
        class="paper-section"
      >
        <!-- 每个量表的名称显示区域 -->
        <div v-if="shouldShowScaleName()" class="scale-name-info">
          <div class="scale-name-container">
            <div class="scale-name-item">
              <h3>{{ paper.name }}</h3>
            </div>
          </div>
        </div>

        <main class="main-content">
          <div class="summary-column">
            <!-- 总体信息大块 -->
            <div v-if="paper && paper.totalResult" class="overall-info-block">
              <!-- 合并的总分和预警等级区块 -->
              <div class="combined-info-card">
                <div class="info-section">
                  <div class="icon-image">
                    <img src="@/assets/images/pReport/total-score.png" alt="总分">
                  </div>
                  <div>
                    <p class="value">{{ (paper.totalResult.score || 0).toFixed(2) }}<span class="value-unit">分</span>
                    </p>
                    <p class="label">总分</p>
                  </div>
                </div>
                <div class="divider" />
                <div class="info-section">
                  <div class="icon-image">
                    <img src="@/assets/images/pReport/warning.png" alt="预警等级">
                  </div>
                  <div>
                    <p class="result-value">{{ getWarnLevelName(paper.totalResult) }}</p>
                    <p class="label">预警等级</p>
                  </div>
                </div>
              </div>

              <!-- 总体测评结果块 -->
              <div class="result-detail-card">
                <h3 class="section-title">总体测评结果</h3>
                <div class="result-content">
                  <div class="result-conclusion">
                    <div class="conclusion-row">
                      <span v-if="shouldDisplayScore(paper.totalResult)" class="total-score">总分：{{ (paper.totalResult.score || 0).toFixed(2) }}分</span>
                      <h4>{{ paper.totalResult.conclusion }}</h4>
                    </div>
                  </div>
                  <div class="result-sections">
                    <div class="result-section">
                      <div class="section-header">
                        <img src="@/assets/images/pReport/myExplain.png" alt="结论" class="section-icon">
                        <h5>结论</h5>
                      </div>
                      <p>{{ paper.totalResult.myExplain }}</p>
                    </div>
                    <div class="result-section">
                      <div class="section-header">
                        <img src="@/assets/images/pReport/advise.png" alt="建议" class="section-icon">
                        <h5>建议</h5>
                      </div>
                      <p>{{ paper.totalResult.advise }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="paper.isChart" class="card">
              <h3 class="section-title">维度测评结果</h3>
              <div :ref="`dimensionChart${paperIndex}`" style="width: 100%; height: 300px;" />
            </div>

            <div v-if="getAdvantageItems(paperIndex).length > 0" class="card">
              <h3 class="section-title">优势项</h3>
              <div class="tags-grid">
                <div v-for="(item, index) in getAdvantageItems(paperIndex)" :key="item.name" class="tag-item">
                  <div class="strength-icon">
                    <img :src="getStrengthIcon(index)" :alt="`优势项${index + 1}`">
                  </div>
                  <span>{{ item.name }}</span>
                </div>
              </div>
            </div>

            <div v-if="getDevelopmentItems(paperIndex).length > 0" class="card">
              <h3 class="section-title">待发展项</h3>
              <div class="development-items-list">
                <div v-for="item in getDevelopmentItems(paperIndex)" :key="item.name" class="development-item">
                  <h4>{{ item.name }}</h4>
                  <p v-html="item.advise || item.myExplain || ''" />
                </div>
              </div>
            </div>
          </div>

          <div class="details-column">
            <section class="card dimension-details-card">
              <div class="deco-shape-1" />
              <div class="deco-shape-2" />

              <h2 class="section-title">测评维度</h2>
              <div class="content-container">
                <div v-for="scale in getDimensionScales(paper)" :key="scale.scaleId" class="dimension-detail-card">
                  <h3 class="dimension-title">{{ scale.name }}</h3>
                  <div class="result-content">
                    <div class="result-conclusion">
                      <div class="conclusion-row">
                        <span v-if="shouldDisplayScore(scale)" class="total-score">得分：{{ (scale.score || 0).toFixed(2) }}分</span>
                        <h4>{{ scale.conclusion || '正常' }}</h4>
                      </div>
                    </div>
                    <div class="result-sections">
                      <div class="result-section">
                        <div class="section-header">
                          <img src="@/assets/images/pReport/d-title.png" alt="维度含义" class="section-icon">
                          <h5>维度含义</h5>
                        </div>
                        <p>{{ scale.overview || '' }}</p>
                      </div>
                      <div class="result-section">
                        <div class="section-header">
                          <img src="@/assets/images/pReport/myExplain.png" alt="结论" class="section-icon">
                          <h5>结论</h5>
                        </div>
                        <p>{{ scale.myExplain || '' }}</p>
                      </div>
                      <div class="result-section">
                        <div class="section-header">
                          <img src="@/assets/images/pReport/advise.png" alt="建议" class="section-icon">
                          <h5>建议</h5>
                        </div>
                        <p>{{ scale.advise || '' }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="hasRecommendedGoods(paperIndex)" class="bottom-banner">
                <!-- 推荐商品展示区域 -->
                <div v-if="mergeRecommendedGoods(paperIndex).length > 0" class="recommended-goods-section">
                  <h4 class="goods-title">推荐商品</h4>
                  <div class="goods-list">
                    <div
                      v-for="goods in mergeRecommendedGoods(paperIndex)"
                      :key="goods.goodsId"
                      class="goods-item"
                      @click="handleGoodsClick(goods)"
                    >
                      <div class="goods-image">
                        <img :src="goods.minor || getDefaultGoodsImage()" :alt="goods.goodsName">
                      </div>
                      <div class="goods-info">
                        <h5 class="goods-name">{{ goods.goodsName }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { mixins } from '@/utils/mixins'
import { getPersonalReport } from '@/api/report'

export default {
  name: 'PReport',
  mixins: [mixins],
  data() {
    return {
      myDataObj: null, // 报告数据对象
      radarChart: null, // 雷达图实例（兼容旧版）
      dimensionCharts: [], // 多个维度图表实例（雷达图或柱状图）
      dimensionsData: {}, // 维度数据（兼容旧版）
      // 用于兼容旧版组件的数据结构
      unitRectDivList: [
        { type: 0, ratio: 1, deepWidth: 0 },
        { type: 1, ratio: 1, deepWidth: 0 },
        { type: 2, ratio: 0.5, deepWidth: 0 },
        { type: 3, ratio: 0, deepWidth: 0 },
        { type: 4, ratio: 0, deepWidth: 0 },
        { type: 5, ratio: 0, deepWidth: 0 },
        { type: 6, ratio: 0, deepWidth: 0 },
        { type: 7, ratio: 0, deepWidth: 0 },
        { type: 8, ratio: 0, deepWidth: 0 },
        { type: 9, ratio: 0, deepWidth: 0 }
      ]

    }
  },
  mounted() {
    // 获取报告数据
    this.ws_getReportDataNetWorkAction()
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // 清理图表实例
    if (this.radarChart) {
      this.radarChart.dispose()
    }
    if (this.dimensionCharts && this.dimensionCharts.length > 0) {
      this.dimensionCharts.forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
    }
    // 移除事件监听
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 获取报告数据
    ws_getReportDataNetWorkAction() {
      const that = this
      getPersonalReport(that.queryParams).then((res) => {
        // 成功
        if (res.code === 200) {
          that.myDataObj = res.data
          that.processReportData()
        } else {
          alert(res.msg || '获取个人报告数据失败')
        }
      }).catch((error) => {
        console.error('网络请求失败:', error)
        alert('网络请求失败，请检查网络连接')
      })
    },

    // 处理报告数据
    processReportData() {
      if (!this.myDataObj) return

      // 转换数据格式以适配新版 UI
      this.convertDataForNewUI()

      // 等待 DOM 更新后初始化图表和内容
      this.$nextTick(() => {
        this.renderDimensionContent()
        this.initRadarChart()
      })
    },

    // 转换数据格式以适配新版 UI
    convertDataForNewUI() {
      if (!this.myDataObj.papers || this.myDataObj.papers.length === 0) return

      // 为每个量表处理数据
      this.myDataObj.papers.forEach((paper) => {
        const dimensionsData = {}

        // 处理总体结果
        if (paper.totalResult) {
          dimensionsData['总分'] = {
            score: paper.totalResult.score || 0,
            status: paper.totalResult.conclusion || '正常',
            color: this.getColorByScore(paper.totalResult.score),
            meaning: paper.totalResult.overview || '',
            conclusion: paper.totalResult.conclusion || '',
            suggestion: paper.totalResult.myExplain || ''
          }
        }

        // 处理维度结果
        if (paper.detailResult && paper.detailResult.scales) {
          paper.detailResult.scales.forEach(scale => {
            dimensionsData[scale.name] = {
              score: scale.score || 0,
              status: scale.conclusion || '正常',
              color: this.getColorByScore(scale.score),
              meaning: scale.overview || '',
              conclusion: scale.conclusion || '',
              suggestion: scale.myExplain || scale.advise || ''
            }
          })
        }

        // 将处理后的数据存储到对应的量表中
        this.$set(paper, 'processedDimensions', dimensionsData)
      })

      // 为了兼容现有代码，保留第一个量表的数据
      if (this.myDataObj.papers.length > 0) {
        this.dimensionsData = this.myDataObj.papers[0].processedDimensions || {}
      }
    },

    // 根据分数确定颜色
    getColorByScore(score) {
      const safeScore = score || 0
      if (safeScore <= 0.3) return 'green'
      if (safeScore <= 0.6) return 'yellow'
      return 'red'
    },

    // 渲染维度内容（现在直接在模板中渲染，这个方法保留用于其他处理）
    renderDimensionContent() {
      // 维度内容现在直接在模板中渲染，无需额外处理
    },

    // 初始化维度图表（支持雷达图、柱状图、饼状图、折线图）
    initRadarChart() {
      if (!this.myDataObj.papers) return

      this.myDataObj.papers.forEach((paper, paperIndex) => {
        // 检查是否需要显示图表
        if (!paper.isChart) return

        const chartRef = this.$refs[`dimensionChart${paperIndex}`]
        if (!chartRef || !chartRef[0]) return

        const chart = this.$echarts.init(chartRef[0])

        // 根据 scaleScoreChart 类型创建不同的图表
        switch (paper.scaleScoreChart) {
          case 1:
            // 雷达图
            this.createRadarChart(chart, paper)
            break
          case 2:
            // 柱状图
            this.createBarChart(chart, paper)
            break
          case 3:
            // 饼状图
            this.createPieChart(chart, paper)
            break
          case 4:
            // 折线图
            this.createLineChart(chart, paper)
            break
          default:
            // 默认使用雷达图
            this.createRadarChart(chart, paper)
            break
        }

        // 存储图表实例以便后续使用
        if (!this.dimensionCharts) {
          this.dimensionCharts = []
        }
        this.dimensionCharts[paperIndex] = chart
      })
    },

    // 创建雷达图
    createRadarChart(chart, paper) {
      const indicators = []
      const values = []

      // 从 detailResult.scales 中获取维度数据（排除总分，且仅包含 displayScore 允许显示的维度）
      if (paper.detailResult && paper.detailResult.scales) {
        const dimensionScales = paper.detailResult.scales.filter(scale => scale.isTotal !== 1 && this.shouldDisplayScore(scale))

        // 去重处理（根据 name 去重）
        const uniqueScales = []
        const seenNames = new Set()

        dimensionScales.forEach(scale => {
          if (!seenNames.has(scale.name)) {
            seenNames.add(scale.name)
            uniqueScales.push(scale)
          }
        })

        uniqueScales.forEach(scale => {
          // 动态计算最大值，取分数的1.5倍作为雷达图的最大值
          const maxValue = Math.max(scale.score * 1.5, 10)
          indicators.push({
            name: scale.name,
            max: maxValue
          })
          values.push(scale.score || 0)
        })
      }

      // 如果没有维度数据，不显示图表
      if (indicators.length === 0) {
        return
      }

      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            // 获取雷达图的所有维度数据
            const data = params.data
            if (data && data.value && indicators.length > 0) {
              let result = `${data.name}<br/>`
              indicators.forEach((indicator, index) => {
                const value = data.value[index] || 0
                result += `${indicator.name}：${value}<br/>`
              })
              return result.slice(0, -5) // 移除最后一个<br/>
            }
            return `${params.name}: ${params.value}`
          }
        },
        radar: {
          name: {
            textStyle: {
              color: '#4B5563',
              fontSize: 13
            }
          },
          indicator: indicators,
          splitArea: {
            areaStyle: {
              color: ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 0)']
            }
          },
          axisLine: {
            lineStyle: {
              color: '#E5E7EB'
            }
          },
          splitLine: {
            lineStyle: {
              color: '#E5E7EB'
            }
          }
        },
        series: [{
          name: '维度测评结果',
          type: 'radar',
          data: [{
            value: values,
            name: '维度测评结果'
          }],
          areaStyle: {
            color: 'rgba(59, 130, 246, 0.2)'
          },
          lineStyle: {
            color: 'rgba(59, 130, 246, 1)',
            width: 2
          },
          itemStyle: {
            color: 'rgba(59, 130, 246, 1)'
          }
        }]
      })
    },

    // 创建柱状图
    createBarChart(chart, paper) {
      const categories = []
      const values = []
      const defaultColor = '#3B82F6' // 默认蓝色

      // 从 detailResult.scales 中获取维度数据（排除总分，且仅包含 displayScore 允许显示的维度）
      if (paper.detailResult && paper.detailResult.scales) {
        const dimensionScales = paper.detailResult.scales.filter(scale => scale.isTotal !== 1 && this.shouldDisplayScore(scale))

        // 去重处理（根据 name 去重）
        const uniqueScales = []
        const seenNames = new Set()

        dimensionScales.forEach(scale => {
          if (!seenNames.has(scale.name)) {
            seenNames.add(scale.name)
            uniqueScales.push(scale)
          }
        })

        uniqueScales.forEach(scale => {
          categories.push(scale.name)
          values.push({
            value: scale.score || 0,
            itemStyle: {
              color: scale.scaleColor || defaultColor
            }
          })
        })
      }

      // 如果没有维度数据，不显示图表
      if (categories.length === 0) {
        return
      }

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function(params) {
            const param = params[0]
            return `${param.name}: ${param.value}`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            interval: 0,
            rotate: categories.length > 6 ? 45 : 0, // 当维度较多时旋转标签
            textStyle: {
              fontSize: 12
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            textStyle: {
              fontSize: 12
            }
          }
        },
        series: [{
          name: '维度测评结果',
          type: 'bar',
          data: values,
          barWidth: '60%',
          label: {
            show: true,
            position: 'top',
            textStyle: {
              fontSize: 12,
              color: '#666'
            }
          }
        }]
      })
    },

    // 创建饼状图
    createPieChart(chart, paper) {
      const pieData = []

      // 从 detailResult.scales 中获取维度数据（排除总分，且仅包含 displayScore 允许显示的维度）
      if (paper.detailResult && paper.detailResult.scales) {
        const dimensionScales = paper.detailResult.scales.filter(scale => scale.isTotal !== 1 && this.shouldDisplayScore(scale))

        // 去重处理（根据 name 去重）
        const uniqueScales = []
        const seenNames = new Set()

        dimensionScales.forEach(scale => {
          if (!seenNames.has(scale.name)) {
            seenNames.add(scale.name)
            uniqueScales.push(scale)
          }
        })

        // 构建饼图数据
        uniqueScales.forEach((scale, index) => {
          pieData.push({
            name: scale.name,
            value: scale.score || 0,
            itemStyle: {
              color: scale.scaleColor || this.getDefaultColor(index)
            }
          })
        })
      }

      // 如果没有维度数据，不显示图表
      if (pieData.length === 0) {
        return
      }

      chart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        series: [{
          name: '维度测评结果',
          type: 'pie',
          radius: '70%', // 普通饼图
          center: ['50%', '50%'], // 居中显示
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}\n{c}分',
            fontSize: 12,
            position: 'outside'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 14,
              fontWeight: 'bold'
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 10
          },
          data: pieData
        }]
      })
    },

    // 创建折线图
    createLineChart(chart, paper) {
      const categories = []
      const values = []
      const defaultColor = '#3B82F6' // 默认蓝色

      // 从 detailResult.scales 中获取维度数据（排除总分，且仅包含 displayScore 允许显示的维度）
      if (paper.detailResult && paper.detailResult.scales) {
        const dimensionScales = paper.detailResult.scales.filter(scale => scale.isTotal !== 1 && this.shouldDisplayScore(scale))

        // 去重处理（根据 name 去重）
        const uniqueScales = []
        const seenNames = new Set()

        dimensionScales.forEach(scale => {
          if (!seenNames.has(scale.name)) {
            seenNames.add(scale.name)
            uniqueScales.push(scale)
          }
        })

        uniqueScales.forEach(scale => {
          categories.push(scale.name)
          values.push(scale.score || 0)
        })
      }

      // 如果没有维度数据，不显示图表
      if (categories.length === 0) {
        return
      }

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: function(params) {
            const param = params[0]
            return `${param.name}: ${param.value}分`
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            interval: 0,
            rotate: categories.length > 6 ? 45 : 0, // 当维度较多时旋转标签
            textStyle: {
              fontSize: 12
            }
          },
          boundaryGap: false
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            textStyle: {
              fontSize: 12
            }
          }
        },
        series: [{
          name: '维度测评结果',
          type: 'line',
          data: values,
          smooth: true, // 平滑曲线
          symbol: 'circle', // 数据点形状
          symbolSize: 8, // 数据点大小
          lineStyle: {
            color: defaultColor,
            width: 3
          },
          itemStyle: {
            color: defaultColor
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [{
                offset: 0,
                color: 'rgba(59, 130, 246, 0.3)'
              }, {
                offset: 1,
                color: 'rgba(59, 130, 246, 0.05)'
              }]
            }
          },
          label: {
            show: true,
            position: 'top',
            formatter: '{c}分',
            textStyle: {
              fontSize: 12,
              color: '#666'
            }
          }
        }]
      })
    },

    // 获取默认颜色（用于饼图）
    getDefaultColor(index) {
      const colors = [
        '#3B82F6', // 蓝色
        '#EF4444', // 红色
        '#10B981', // 绿色
        '#F59E0B', // 黄色
        '#8B5CF6', // 紫色
        '#06B6D4', // 青色
        '#F97316', // 橙色
        '#84CC16', // 草绿色
        '#EC4899', // 粉色
        '#6B7280' // 灰色
      ]
      return colors[index % colors.length]
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.dimensionCharts && this.dimensionCharts.length > 0) {
        this.dimensionCharts.forEach(chart => {
          if (chart) {
            chart.resize()
          }
        })
      }
    },

    // 导出报告（兼容旧版功能）
    ws_exportReportBtnClickAction() {
      // 这里可以添加导出功能
      console.log('导出报告功能')
    },

    // 获取报告标题
    getReportTitle() {
      if (!this.myDataObj) return '心理健康测评报告'

      const hasActivityName = this.myDataObj.activityName && this.myDataObj.activityName.trim() !== ''
      const paperCount = this.myDataObj.papers ? this.myDataObj.papers.length : 0

      if (hasActivityName) {
        return this.myDataObj.activityName
      } else {
        if (paperCount === 1) {
          return this.myDataObj.papers[0].name
        } else {
          return '测评报告'
        }
      }
    },

    // 是否显示量表名称
    shouldShowScaleName() {
      if (!this.myDataObj || !this.myDataObj.papers) return false

      const hasActivityName = this.myDataObj.activityName && this.myDataObj.activityName.trim() !== ''
      const paperCount = this.myDataObj.papers.length

      // 有 activityName 时，总是显示量表名称
      if (hasActivityName) {
        return true
      }

      // 没有 activityName 时，只有多份量表才显示量表名称
      return paperCount > 1
    },

    // 获取优势项（直接使用 papers[i].strengths 数组）
    getAdvantageItems(paperIndex = 0) {
      if (!this.myDataObj.papers || !this.myDataObj.papers[paperIndex]) return []

      const paper = this.myDataObj.papers[paperIndex]
      return paper.strengths || []
    },

    // 获取待发展项（直接使用 papers[i].developmentAreas 数组）
    getDevelopmentItems(paperIndex = 0) {
      if (!this.myDataObj.papers || !this.myDataObj.papers[paperIndex]) return []

      const paper = this.myDataObj.papers[paperIndex]
      return paper.developmentAreas || []
    },

    // 获取学校信息（合并学校、年级、班级）
    getSchoolInfo() {
      if (!this.myDataObj || !this.myDataObj.evaluator) return ''

      const evaluator = this.myDataObj.evaluator
      const parts = []

      if (evaluator.schoolName) {
        parts.push(evaluator.schoolName)
      }

      if (evaluator.gradeName) {
        parts.push(evaluator.gradeName)
      }

      if (evaluator.className) {
        parts.push(evaluator.className)
      }

      return parts.join(' ')
    },

    // 获取预警等级名称，无数据时显示"正常"
    getWarnLevelName(totalResult) {
      if (!totalResult) return '正常'
      return totalResult.warnLevelName || '正常'
    },

    // 获取优势项图标，循环使用1-6的图标
    getStrengthIcon(index) {
      const iconIndex = (index % 6) + 1
      return require(`@/assets/images/pReport/strength-${iconIndex}.png`)
    },

    // 获取维度数据（排除总分）
    getDimensionScales(paper) {
      if (!paper || !paper.detailResult || !paper.detailResult.scales) return []
      // 返回所有维度（包括 displayScore 为 false 的维度，因为需要显示维度名称和其他信息）
      return paper.detailResult.scales.filter(scale => scale.isTotal !== 1)
    },

    // 合并推荐商品（量表级别 + 维度级别，按goodsId去重）
    mergeRecommendedGoods(paperIndex = 0) {
      if (!this.myDataObj.papers || !this.myDataObj.papers[paperIndex]) return []

      const paper = this.myDataObj.papers[paperIndex]
      const allGoods = []
      const goodsIdSet = new Set()

      // 添加量表级别的推荐商品
      if (paper.recommendedGoods && Array.isArray(paper.recommendedGoods)) {
        paper.recommendedGoods.forEach(goods => {
          if (goods && goods.goodsId && !goodsIdSet.has(goods.goodsId)) {
            allGoods.push(goods)
            goodsIdSet.add(goods.goodsId)
          }
        })
      }

      // 添加维度级别的推荐商品
      if (paper.detailResult && paper.detailResult.scales && Array.isArray(paper.detailResult.scales)) {
        paper.detailResult.scales.forEach(scale => {
          if (scale.recommendedGoods && Array.isArray(scale.recommendedGoods)) {
            scale.recommendedGoods.forEach(goods => {
              if (goods && goods.goodsId && !goodsIdSet.has(goods.goodsId)) {
                allGoods.push(goods)
                goodsIdSet.add(goods.goodsId)
              }
            })
          }
        })
      }

      return allGoods
    },

    // 判断是否有推荐商品
    hasRecommendedGoods(paperIndex = 0) {
      const mergedGoods = this.mergeRecommendedGoods(paperIndex)
      return mergedGoods.length > 0
    },

    // 处理商品点击
    handleGoodsClick(goods) {
      // 这里可以根据需求处理商品点击事件，比如跳转到商品详情页
      console.log('点击商品:', goods)
      // 示例：跳转到商品详情页面
      // if (goods.goodsUrl) {
      //   window.open(goods.goodsUrl, '_blank')
      // }
    },

    // 获取默认商品图片
    getDefaultGoodsImage() {
      // 可以返回一个默认的商品图片路径
      return require('@/assets/images/pReport/avatar.png')
    },

    // 判断是否应该显示分数(兼容布尔型和整型)
    shouldDisplayScore(item) {
      if (!item) return false

      // 如果没有 displayScore 属性,默认显示
      if (item.displayScore === undefined || item.displayScore === null) {
        return true
      }

      // 布尔型: true 显示, false 不显示
      if (typeof item.displayScore === 'boolean') {
        return item.displayScore
      }

      // 整型: 1 显示, 0 不显示
      if (typeof item.displayScore === 'number') {
        return item.displayScore === 1
      }

      // 其他情况,默认显示
      return true
    }
  }
}
</script>

<style>
/* --- 基本样式和重置 --- */
:root {
  --blue-50: #EFF6FF;
  --blue-100: #DBEAFE;
  --blue-500: #3B82F6;
  --blue-600: #2563EB;
  --yellow-50: #FFFBEB;
  --yellow-100: #FEF3C7;
  --yellow-400: #FBBF24;
  --yellow-500: #F59E0B;
  --yellow-900: #78350F;
  --green-50: #F0FDF4;
  --green-100: #DCFCE7;
  --green-300: #86EFAC;
  --green-500: #22C55E;
  --green-600: #16A34A;
  --red-100: #FEE2E2;
  --red-300: #FCA5A5;
  --red-500: #EF4444;
  --red-600: #DC2626;
  --orange-50: #FFF7ED;
  --orange-500: #F97316;
  --gray-100: #F3F4F6;
  --gray-200: #E5E7EB;
  --gray-300: #D1D5DB;
  --gray-500: #6B7280;
  --gray-600: #4B5563;
  --gray-700: #374151;
  --gray-800: #1F2937;
  --white: #FFFFFF;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html,
body {
  overflow-x: hidden;
  overflow-y: auto;
}

/* --- 页面包装器 --- */
.page-wrapper {
  background-image: url('~@/assets/images/pReport/bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  width: 100%;
  min-height: 100vh;
}

h2,
h3,
h4 {
  margin: 0;
  font-weight: 600;
  color: var(--gray-800);
}

/* --- 布局容器 --- */
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem;
  font-family: 'PingFang SC', 'Helvetica Neue', 'Microsoft YaHei', sans-serif;
  color: var(--gray-700);
  line-height: 1.5;
}

/* --- 头部 --- */
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.header h1 {
  font-size: 1.25rem;
  text-align: center;
  flex-grow: 1;
}

/* --- 测评者信息 --- */
.user-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background-image: url('~@/assets/images/pReport/profile-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  position: relative;
}

/* --- 总体信息大块 --- */
.overall-info-block {
  background-image: url('~@/assets/images/pReport/profile-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  padding: 1rem;
  position: relative;
}

/* --- 总体测评结果卡片 --- */
.result-detail-card {
  background-image: url('~@/assets/images/pReport/content-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  padding: 0.75rem;
  margin-top: 1rem;
  position: relative;
}

/* --- 维度详情卡片 --- */
.dimension-detail-card {
  background-image: url('~@/assets/images/pReport/content-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  position: relative;
}

.user-info .avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.user-info .avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details {
  flex: 1;
}

.user-info h2 {
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: var(--gray-800);
}

.user-info-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.user-info-list p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--gray-600);
  line-height: 1.4;
}

/* --- 量表区域 --- */
.paper-section {
  margin-bottom: 3rem;
}

.paper-section:last-child {
  margin-bottom: 0;
}

/* --- 量表名称信息 --- */
.scale-name-info {
  margin-bottom: 1.5rem;
}

.scale-name-container {
  padding: 0;
}

.scale-name-item {
  padding: 0;
}

.scale-name-item h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-800);
  text-align: center;
}

/* --- 主网格布局 --- */
.main-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* --- 卡片样式 --- */
.card {
  background-image: url('~@/assets/images/pReport/profile-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  position: relative;
}

.info-cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.overall-info-block .info-cards-grid {
  margin-bottom: 0;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: var(--white);
  padding: 1.25rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
}

.overall-info-block .info-card {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

/* --- 合并的信息卡片样式 --- */
.combined-info-card {
  display: flex;
  align-items: center;
  background-image: url('~@/assets/images/pReport/content-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  backdrop-filter: blur(10px);
  padding: 0.75rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-sm);
  margin-bottom: 0;
}

.info-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.divider {
  width: 1px;
  height: 60px;
  background-color: rgba(0, 0, 0, 0.2);
  margin: 0 0.75rem;
  flex-shrink: 0;
}

.info-card .icon-wrapper {
  padding: 0.75rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-card .icon-image,
.info-section .icon-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-card .icon-image img,
.info-section .icon-image img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.info-card .icon-wrapper.blue {
  background-color: var(--blue-100);
  color: var(--blue-500);
}

.info-card .icon-wrapper.yellow {
  background-color: var(--yellow-100);
  color: var(--yellow-500);
}

.info-card p,
.info-section p {
  margin: 0;
}

.info-card .value,
.info-section .value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--gray-800);
}

.info-card .value-unit,
.info-section .value-unit {
  font-size: 0.875rem;
  font-weight: 400;
  margin-left: 0.25rem;
}

.info-card .label,
.info-section .label {
  font-size: 0.875rem;
  color: var(--gray-500);
}

.info-card .result-value,
.info-section .result-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--gray-800);
}

.tags-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.tag-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.tag-item .icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-item .icon-wrapper.green {
  background-color: var(--green-100);
  color: var(--green-600);
}

.tag-item .icon-wrapper.yellow {
  background-color: var(--yellow-100);
  color: var(--yellow-500);
}

.tag-item .strength-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-item .strength-icon img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.tag-item span {
  font-size: 0.875rem;
  color: var(--gray-600);
}

.card h3 {
  margin-bottom: 1rem;
}

/* --- 总体测评结果样式 --- */
.result-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.result-conclusion {
  text-align: left;
  padding: 0.5rem 0;
}

.conclusion-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
}

.total-score {
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-700);
}

.result-conclusion h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-700);
}

.result-sections {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.result-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

.section-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-800);
}

.result-section p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--gray-600);
  padding-left: 28px;
}

/* --- 维度内容样式 --- */
.dimension-detail-card:last-child {
  margin-bottom: 0;
}

/* 确保维度详情卡片的结论行样式与总体测评结果一致 */
.dimension-detail-card .result-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.dimension-detail-card .result-conclusion {
  text-align: left;
  padding: 0.5rem 0;
}

.dimension-detail-card .conclusion-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
}

.dimension-detail-card .total-score {
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-700);
}

.dimension-detail-card .result-conclusion h4 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--gray-700);
}

/* --- 待发展项列表样式 --- */
.development-items-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.development-item h4 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.development-item h4::before {
  content: '';
  display: block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--yellow-400);
}

.development-item p {
  font-size: 0.875rem;
  color: var(--gray-600);
  line-height: 1.6;
  margin: 0;
  padding-left: 13px;
}

/* --- 右侧详情区域 (在移动端是普通卡片) --- */
.dimension-details-card {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 1rem !important;
}

.dimension-details-card h2 {
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 1rem;
  padding: 0;
}

/* 动态样式类 - 使用 >>> 确保能应用到动态生成的内容 */
/* 动态样式类 */
.text-red {
  color: var(--red-500);
}

.bg-red {
  background-color: var(--red-100);
}

.border-red {
  border-color: var(--red-500);
}

.text-yellow {
  color: var(--yellow-500);
}

.bg-yellow {
  background-color: var(--yellow-100);
}

.border-yellow {
  border-color: var(--yellow-500);
}

.text-green {
  color: var(--green-500);
}

.bg-green {
  background-color: var(--green-100);
}

.border-green {
  border-color: var(--green-500);
}

/* --- 图形可视化 --- */
.curved-graph-bg {
  height: 10rem;
  width: 100%;
  background: linear-gradient(180deg, rgba(235, 248, 255, 0.1) 0%, rgba(213, 239, 254, 0.9) 100%);
  border-bottom-left-radius: 999px;
  border-bottom-right-radius: 999px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1rem;
}

.graph-dashed-line {
  width: 150%;
  height: 6rem;
  border-top: 4px dashed var(--gray-300);
  border-radius: 9999px 9999px 0 0;
  position: absolute;
  bottom: 0;
}

.graph-color-path {
  width: 100%;
  height: 8rem;
  position: absolute;
  bottom: 0;
  background: linear-gradient(to right, var(--green-300), var(--yellow-300), var(--red-300));
  clip-path: path('M0,128 Q300,-50 600,128 L600,128 L0,128 Z');
  transform: scale(0.6, 0.4) translate(18%, 10%);
  opacity: 0.7;
}

.score-marker-container {
  position: absolute;
  bottom: 2.5rem;
  width: 100%;
}

.score-marker {
  position: relative;
}

.score-box {
  background-color: var(--white);
  box-shadow: var(--shadow-lg);
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 6rem;
  transform: translateX(-50%);
}

.score-box .score-value {
  font-weight: 700;
  font-size: 1.5rem;
}

.score-box .score-status {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
}

.score-marker-dot {
  width: 1rem;
  height: 1rem;
  background-color: var(--white);
  box-shadow: var(--shadow-md);
  border-radius: 9999px;
  position: absolute;
  bottom: -0.5rem;
  left: 50%;
  transform: translateX(-50%);
  border-width: 2px;
  border-style: solid;
}

.dimension-content h3 {
  display: block;
  font-size: 1.125rem;
  margin: 0 0 1rem 0;
}

.dimension-content h4 {
  margin-bottom: 0.5rem;
}

.dimension-content p {
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
}

.dimension-content .conclusion-text {
  font-weight: 700;
  color: var(--blue-600);
}

/* --- 底部横幅 --- */
.bottom-banner {
  position: relative;
  z-index: 10;
  background-image: linear-gradient(to right, var(--yellow-50), var(--orange-50));
  padding: 1.5rem;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  text-align: center;
  margin-top: 2rem;
}

.bottom-banner p {
  font-size: 1.25rem;
  font-weight: 800;
  background-image: linear-gradient(to right, var(--orange-500), var(--red-500));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
}

.bottom-banner button {
  background-color: var(--yellow-400);
  color: var(--white);
  font-weight: 700;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  box-shadow: var(--shadow-md);
  border: none;
  cursor: pointer;
  transition: transform 0.2s;
}

.bottom-banner button:hover {
  transform: scale(1.05);
}

/* --- 推荐商品样式 --- */
.banner-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  text-align: center;
  width: 100%;
}

.recommended-goods-section {
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  width: 100%;
}

.goods-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-800);
  margin: 0 0 1rem 0;
  text-align: center;
}

.goods-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.goods-item {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
}

.goods-item:hover {
  background-color: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.goods-image {
  width: 80px;
  height: 80px;
  border-radius: 0.5rem 0 0 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
}

.goods-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.goods-info {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  height: 80px;
  padding: 1rem 1rem 1rem 0;
}

.goods-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--gray-800);
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

/* --- 装饰性背景 --- */
.deco-shape-1 {
  display: none;
}

.deco-shape-2 {
  display: none;
}

.card.dimension-details-card {
  position: relative;
  overflow: hidden;
}

/* --- 响应式设计 --- */
@media (min-width: 640px) {
  .container {
    padding: 1.5rem;
  }

  .bottom-banner {
    flex-direction: row;
    text-align: left;
  }

  .banner-content {
    flex-direction: row;
    text-align: left;
  }

  .goods-item {
    gap: 1.125rem;
  }

  .goods-image {
    width: 90px;
    height: 90px;
    border-radius: 0.75rem 0 0 0.75rem;
  }

  .goods-name {
    font-size: 1rem;
  }

  .goods-info {
    height: 90px;
    padding: 1.125rem 1.125rem 1.125rem 0;
    display: flex;
    align-items: center;
  }
}

@media (min-width: 1024px) {
  .container {
    padding: 2rem;
  }

  .header h1 {
    font-size: 1.5rem;
  }

  .main-content {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 2rem;
    align-items: flex-start;
  }

  .summary-column {
    grid-column: span 4;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .details-column {
    grid-column: span 8;
  }

  .card.dimension-details-card {
    padding: 1.5rem 1.5rem 1.5rem;
  }

  .deco-shape-1 {
    display: block;
    position: absolute;
    top: -10rem;
    right: -10rem;
    width: 20rem;
    height: 0;
    background-color: var(--blue-50);
    border-radius: 9999px;
    opacity: 0.5;
    z-index: 0;
  }

  .deco-shape-2 {
    display: block;
    position: absolute;
    bottom: -5rem;
    left: -5rem;
    width: 15rem;
    height: 0;
    background-color: var(--green-50);
    border-radius: 9999px;
    opacity: 0.5;
    z-index: 0;
  }

  .dimension-details-card>* {
    position: relative;
    z-index: 10;
  }

  .goods-item {
    gap: 1.25rem;
  }

  .goods-image {
    width: 100px;
    height: 100px;
    border-radius: 0.75rem 0 0 0.75rem;
  }

  .goods-name {
    font-size: 1.05rem;
  }

  .goods-info {
    height: 100px;
    padding: 1.25rem 1.25rem 1.25rem 0;
    display: flex;
    align-items: center;
  }

  }
</style>
